import { useStravaDestination } from "strava-acquisition/hooks/use-strava-destination";
import { StravaConnectButton } from "strava-acquisition/components/strava-connect-button";
import { CollectionListHandle } from "strava-acquisition/types";
import { DEFAULT_COLLECTION_LIST_HANDLE } from "strava-acquisition/constants";
import styles from "../landing-page-four.module.css";

/**
 * Regex pattern for validating UUID v4 format.
 * Collection IDs are expected to be UUIDs.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Validates that a collection ID is in the expected UUID format.
 * Logs a warning in development if the format is invalid.
 */
const validateCollectionId = (collectionId: string | undefined): boolean => {
  if (!collectionId) return true; // Optional prop, undefined is valid

  const isValid = UUID_PATTERN.test(collectionId);
  if (!isValid && process.env.NODE_ENV === "development") {
    console.warn(
      `StravaSection: collectionId "${collectionId}" does not match expected UUID format. ` +
      `Expected format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`
    );
  }
  return isValid;
};

interface StravaSectionProps {
  /** Optional collection ID to navigate to after Strava flow (should be a valid UUID) */
  collectionId?: string;
  /** Handle for the collection list page (defaults to "A") */
  collectionListHandle?: CollectionListHandle;
}

/**
 * Landing page section encouraging users to connect with Strava.
 * Features Strava brand colors and saves destination before OAuth flow.
 */
export const StravaSection = ({
  collectionId,
  collectionListHandle = DEFAULT_COLLECTION_LIST_HANDLE,
}: StravaSectionProps) => {
  const { saveDestination } = useStravaDestination();

  // Validate collectionId format in development
  validateCollectionId(collectionId);

  const handleConnect = () => {
    saveDestination(collectionId ?? null, collectionListHandle);
  };

  return (
    <div className={styles.stravaSectionContainer}>
      <div className={styles.stravaContent}>
        <h2 className={styles.stravaTitle}>Connect Your Strava</h2>
        <p className={styles.stravaDescription}>
          Import your rides, runs, and adventures directly from Strava. Turn
          your favorite activities into stunning wall art with just a few
          clicks. Your data stays private and secure.
        </p>
        <StravaConnectButton
          className={styles.stravaButton}
          onConnect={handleConnect}
          returnTo="/strava/activities"
        />
      </div>
    </div>
  );
};
